import { FC, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	IconButton,
	MenuItem,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';
import { Delete, Save } from '@mui/icons-material';
import {
	deletePgServiceTypeMappingRequest,
	getPgServiceTypeMappingsRequest,
	upsertPgServiceTypeMappingRequest,
} from 'redux/reducers/pgSettings/reducer';
import {
	selectPgSaveLoading,
	selectPgServiceTypeMappings,
	selectPgServiceTypeMappingsLoading,
} from 'redux/reducers/pgSettings/selectors';
import { TPgService } from 'services/api/paymentGateway/types';
import { ConfirmationDialog } from 'ui/ConfirmationDialog';
import { PgPanel } from '../../common/PgPanel';
import { PG_KNOWN_SERVICE_TYPES, PG_SERVICE_TYPE_REGEX } from '../../constants';

type TServiceTypeMappingsProps = {
	services: TPgService[] | null;
};

/** The gateway normalizes `service_type` the same way (trim, lower case). */
const normalizeServiceType = (value: string): string => value.trim().toLowerCase();

const serviceLabel = (service: TPgService) =>
	`${service.name} (${service.code})${service.isActive ? '' : ' — inactive'}`;

/**
 * EAS `invoice_list_info.service_type` → gateway service. An invoice whose service type is not
 * mapped cannot be paid through the gateway.
 */
export const ServiceTypeMappings: FC<TServiceTypeMappingsProps> = ({ services }) => {
	const dispatch = useDispatch();
	const mappings = useSelector(selectPgServiceTypeMappings);
	const loading = useSelector(selectPgServiceTypeMappingsLoading);
	const saving = useSelector(selectPgSaveLoading('serviceTypeMapping'));

	// Draft service per existing service type (only rows the admin changed).
	const [drafts, setDrafts] = useState<Record<string, number>>({});
	const [newType, setNewType] = useState('');
	const [newServiceId, setNewServiceId] = useState<number | ''>('');
	const [typeToDelete, setTypeToDelete] = useState<string | null>(null);

	useEffect(() => {
		dispatch(getPgServiceTypeMappingsRequest());
	}, [dispatch]);

	// A saved row drops its draft once the list reloads with the new value.
	const [syncedMappings, setSyncedMappings] = useState(mappings);
	if (mappings !== syncedMappings) {
		setSyncedMappings(mappings);
		setDrafts((current) => {
			const next = { ...current };
			(mappings ?? []).forEach((mapping) => {
				if (next[mapping.serviceType] === mapping.serviceId) delete next[mapping.serviceType];
			});
			return next;
		});
	}

	// Only invoice services can be mapped (EU DDP prepayment is not an EAS invoice).
	const invoiceServices = useMemo(
		() => (services ?? []).filter((service) => service.kind === 'invoice'),
		[services],
	);

	const mappedTypes = useMemo(
		() => new Set((mappings ?? []).map((mapping) => mapping.serviceType)),
		[mappings],
	);
	const unmappedKnownTypes = PG_KNOWN_SERVICE_TYPES.filter((type) => !mappedTypes.has(type));

	const normalizedNewType = normalizeServiceType(newType);
	const newTypeError =
		normalizedNewType && !PG_SERVICE_TYPE_REGEX.test(normalizedNewType)
			? 'Lowercase letters, digits and "_", up to 40 characters'
			: normalizedNewType && mappedTypes.has(normalizedNewType)
				? 'Already mapped: change it in the table'
				: '';

	const handleSaveRow = (serviceType: string) => {
		const serviceId = drafts[serviceType];
		if (!serviceId) return;
		dispatch(upsertPgServiceTypeMappingRequest({ serviceType, body: { serviceId } }));
	};

	const handleAdd = () => {
		if (!normalizedNewType || newTypeError || !newServiceId) return;
		dispatch(
			upsertPgServiceTypeMappingRequest({
				serviceType: normalizedNewType,
				body: { serviceId: newServiceId },
			}),
		);
		setNewType('');
		setNewServiceId('');
	};

	const handleConfirmDelete = () => {
		if (typeToDelete) dispatch(deletePgServiceTypeMappingRequest(typeToDelete));
		setTypeToDelete(null);
	};

	return (
		<PgPanel
			title="EAS service type → service"
			actions={saving || loading ? <CircularProgress size={18} /> : undefined}
		>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
				Every EAS invoice has a <b>service_type</b> (view <b>invoice_list_info</b>). The gateway
				finds the invoice service, its fee rules and the customer&apos;s payment methods through
				this mapping. Invoices with an unmapped service type cannot be paid.
			</Typography>
			<Table size="small">
				<TableHead>
					<TableRow>
						<TableCell sx={{ fontWeight: 'bold' }}>EAS service type</TableCell>
						<TableCell sx={{ fontWeight: 'bold' }}>Service</TableCell>
						<TableCell sx={{ fontWeight: 'bold' }} align="right">
							Actions
						</TableCell>
					</TableRow>
				</TableHead>
				<TableBody>
					{(mappings ?? []).map((mapping) => {
						const draft = drafts[mapping.serviceType];
						const selected = draft ?? mapping.serviceId;
						const changed = draft !== undefined && draft !== mapping.serviceId;
						const knownService = invoiceServices.some((s) => s.id === selected);
						return (
							<TableRow key={mapping.serviceType}>
								<TableCell sx={{ fontFamily: 'monospace' }}>{mapping.serviceType}</TableCell>
								<TableCell>
									<TextField
										select
										size="small"
										value={knownService ? selected : ''}
										onChange={(event) =>
											setDrafts((current) => ({
												...current,
												[mapping.serviceType]: Number(event.target.value),
											}))
										}
										sx={{ minWidth: 280 }}
										helperText={knownService ? undefined : `Service ${mapping.serviceCode}`}
									>
										{invoiceServices.map((service) => (
											<MenuItem key={service.id} value={service.id}>
												{serviceLabel(service)}
											</MenuItem>
										))}
									</TextField>
								</TableCell>
								<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
									<IconButton
										color="primary"
										title="Save"
										disabled={!changed || saving}
										onClick={() => handleSaveRow(mapping.serviceType)}
									>
										<Save fontSize="small" />
									</IconButton>
									<IconButton
										color="error"
										title="Remove mapping"
										disabled={saving}
										onClick={() => setTypeToDelete(mapping.serviceType)}
									>
										<Delete fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						);
					})}
					{!loading && mappings?.length === 0 && (
						<TableRow>
							<TableCell colSpan={3}>
								<Typography variant="body2" color="text.secondary">
									No mappings yet.
								</Typography>
							</TableCell>
						</TableRow>
					)}
					<TableRow>
						<TableCell>
							<TextField
								size="small"
								placeholder="e.g. eas"
								value={newType}
								onChange={(event) => setNewType(event.target.value)}
								error={!!newTypeError}
								helperText={newTypeError || undefined}
								sx={{ minWidth: 160 }}
							/>
						</TableCell>
						<TableCell>
							<TextField
								select
								size="small"
								label="Service"
								value={newServiceId}
								onChange={(event) => setNewServiceId(Number(event.target.value))}
								sx={{ minWidth: 280 }}
							>
								{invoiceServices.map((service) => (
									<MenuItem key={service.id} value={service.id}>
										{serviceLabel(service)}
									</MenuItem>
								))}
							</TextField>
						</TableCell>
						<TableCell align="right">
							<Button
								variant="contained"
								size="small"
								onClick={handleAdd}
								disabled={!normalizedNewType || !!newTypeError || !newServiceId || saving}
							>
								Add mapping
							</Button>
						</TableCell>
					</TableRow>
				</TableBody>
			</Table>
			{unmappedKnownTypes.length > 0 && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, flexWrap: 'wrap' }}>
					<Typography variant="body2" color="text.secondary">
						Known EAS service types without a mapping:
					</Typography>
					{unmappedKnownTypes.map((type) => (
						<Chip
							key={type}
							label={type}
							size="small"
							color="warning"
							variant="outlined"
							onClick={() => setNewType(type)}
						/>
					))}
				</Box>
			)}
			<ConfirmationDialog
				open={!!typeToDelete}
				setClose={() => setTypeToDelete(null)}
				handleConfirm={handleConfirmDelete}
				title="Remove mapping"
				text={`Invoices with service type <b>${typeToDelete ?? ''}</b> will not be payable through the gateway until it is mapped again. Remove the mapping?`}
			/>
		</PgPanel>
	);
};
